import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { fmtDate } from "../lib/constants";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function CalendarPage() {
  const [tasks, setTasks] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", due_at: "", description: "" });
  const navigate = useNavigate();

  const load = () => api.get("/tasks").then((r) => setTasks(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", form);
      toast.success("Task created"); setShow(false); setForm({ title: "", due_at: "", description: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (t) => { await api.put(`/tasks/${t.id}`, { completed: !t.completed }); load(); };
  const del = async (id) => { if (!window.confirm("Delete this task?")) return; await api.delete(`/tasks/${id}`); load(); };

  const upcoming = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-head font-extrabold tracking-tight">Task Calendar</h1>
          <p className="text-muted-foreground" data-testid="calendar-timezone">Callbacks & reminders · Australia/Melbourne</p>
        </div>
        <button data-testid="new-task-btn" onClick={() => setShow(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">
          <Icons.Plus size={18} /> New Task
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
        <div className="bg-card border border-border rounded-md p-5 shadow-sm min-w-0">
          <h3 className="font-head font-semibold mb-3">Upcoming ({upcoming.length})</h3>
          <div className="space-y-2" data-testid="upcoming-tasks">
            {upcoming.map((t) => (
              <div key={t.id} className="flex items-start gap-3 p-3 rounded-md border border-border">
                <input type="checkbox" checked={false} onChange={() => toggle(t)} className="mt-1" data-testid={`task-check-${t.id}`} />
                <div className="flex-1 min-w-0 break-words">
                  <div className="font-medium text-sm" data-testid={`task-name-${t.id}`}>{t.title}</div>
                  <div className="text-xs text-primary" data-testid={`task-date-${t.id}`}>{fmtDate(t.due_at)} · Melbourne</div>
                  {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
                  {t.lead_id && <button onClick={() => navigate(`/app/leads/${t.lead_id}`)} data-testid={`task-lead-${t.id}`} className="text-xs text-secondary underline mt-1">View lead</button>}
                </div>
                <button onClick={() => del(t.id)} data-testid={`task-delete-${t.id}`} aria-label="Delete task" className="text-destructive"><Icons.Trash2 size={15} /></button>
              </div>
            ))}
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground">No upcoming tasks</p>}
          </div>
        </div>
        <div className="bg-card border border-border rounded-md p-5 shadow-sm min-w-0">
          <h3 className="font-head font-semibold mb-3">Completed ({done.length})</h3>
          <div className="space-y-2">
            {done.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 rounded-md border border-border opacity-60">
                <input type="checkbox" checked onChange={() => toggle(t)} data-testid={`task-reopen-${t.id}`} aria-label="Reopen task" />
                <div className="flex-1 min-w-0 break-words text-sm line-through" data-testid={`completed-task-name-${t.id}`}>{t.title}</div>
                <button onClick={() => del(t.id)} data-testid={`completed-task-delete-${t.id}`} aria-label="Delete completed task" className="text-destructive"><Icons.Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-card rounded-md w-full max-w-md p-6 shadow-lg">
            <h2 className="text-xl font-head font-extrabold mb-4">New Task</h2>
            <label className="label-caps">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full mt-1 mb-3 px-3 py-2 rounded-md border border-input bg-background" data-testid="task-title" />
            <label className="label-caps">Due Date & Time · Australia/Melbourne</label>
            <input required type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })}
              className="w-full mt-1 mb-3 px-3 py-2 rounded-md border border-input bg-background" data-testid="task-due" />
            <label className="label-caps">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description"
              className="w-full mt-1 mb-4 px-3 py-2 rounded-md border border-input bg-background" rows={2} />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShow(false)} data-testid="task-cancel" className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold" data-testid="task-submit">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
